/**
 * Das Fenster „Lage“ unter dem Kopf. (08.10.2026, Martin, Richtung C)
 *
 * Der Kopf ist ruhig und fast leer: Menü, Stern, Name, Glocke. Was früher als Punkte im
 * Kopf stand, klappt jetzt beim Tippen auf die Glocke auf — Telefon: unter dem Kopf über die
 * Breite, Rechner: rechts, 312 Punkte. Die Zeile „N neue Meldungen ansehen“
 * darin führt in die bestehende Glocken-Liste (lib/glocke.ts): es gibt keine zweite Glocke.
 *
 * 11.10.2026 (Martin: „Der Header sieht immer noch schrecklich aus mit diesen vielen
 * Punkten“): Die Tafel trägt nicht mehr die vier Zahlen (die stehen jetzt auf Zuhause,
 * lib/entwicklungs-ticker.ts), sondern die Hinweise, die vorher als Chips im Kopf standen —
 * je eine Zeile, nur solange sie etwas zu sagen hat:
 *   Spiegel      „Dein Spiegel liegt bereit“   -> öffnet den Spiegel
 *   Mac          „Mac verbunden“               -> die Verbindungen
 *   Aufgaben     „2 Aufgaben im Hintergrund“   -> die Aufgaben
 * Jeder Hinweis steht NUR hier (kein zweiter Weg). Der Spiegel zählt an der Glocke nicht mit.
 *
 * Besitzer (je ein `hidden`, nie zwei):
 *   #topbar-spiegel          app.ts (zeigeSpiegelHinweis)
 *   #topbar-mac              lib/mac-zeichen.ts
 *   #topbar-tasks-pulse      lib/tasks-view.ts
 *   #topbar-lage             diese Datei                 (offen/zu)
 *   #topbar-lage-meldungen   lib/glocke.ts               (zeichneZahl: liegt etwas in der Liste?)
 *   #topbar-glocke           lib/glocke.ts               (da, sobald Meldungen ODER ein Hinweis da sind)
 * Wer seinen Schalter umlegt, ruft danach `lageGeaendert()`: die Glocke prüft sich neu, und eine
 * offene Tafel, in der nichts mehr steht, klappt zu.
 *
 * Gibt es keinen Hinweis (nur Meldungen oder gar nichts), tippt die Glocke wie vorher direkt die Liste an.
 *
 * Bedienung: Escape schließt (Fokus zurück auf die Glocke), Tippen außerhalb schließt,
 * Tab aus dem Fenster heraus schließt. Auch das Antippen einer Hinweiszeile (Spiegel, Mac, Aufgaben) schließt — und zwar
 * BEVOR die Zeile ihre Ansicht öffnet (Integrationsprüfung 11.10.2026, F3): das Fenster liegt über der Ansicht (seit 22.10.2026 auf --z-tafel, auch über Vollseiten) und
 * verdeckte sonst das, wohin der Tipp führen sollte. Wurde die Zeile mit der Tastatur ausgelöst (Enter/Leertaste), kehrt der
 * Fokus zur Glocke zurück — wie bei Escape; bei Maus und Fingern bleibt er, wohin die Ansicht ihn setzt. `aria-expanded` an der Glocke zeigt, ob es offen ist
 * (`aria-controls` steht im Markup). Der Fokus bleibt auf der Glocke — ein Aufklapp-Fenster,
 * kein Dialog.
 */

const GLOCKE = "topbar-glocke";
const FENSTER = "topbar-lage";
const MELDUNGEN = "topbar-lage-meldungen";

/** Die Hinweiszeilen der Tafel (Ids der Besitzer, siehe oben). */
const LAGE_ZEILEN: readonly string[] = ["topbar-spiegel", "topbar-mac", "topbar-tasks-pulse"];

/** Ereignis: ein Hinweis der Tafel ist erschienen oder weggegangen. */
export const LAGE_GEAENDERT = "vespera:lage-geaendert";

/** Der Satz der Aufgaben-Zeile: „1 Aufgabe im Hintergrund“, „2 Aufgaben im Hintergrund“. */
export function aufgabenSatz(n: number): string {
  return n === 1 ? "1 Aufgabe im Hintergrund" : `${n} Aufgaben im Hintergrund`;
}

/** Gibt es einen Hinweis zu zeigen? (Mindestens eine der Zeilen ist nicht versteckt.) */
export function lageDa(): boolean {
  return LAGE_ZEILEN.some((id) => {
    const z = document.getElementById(id);
    return !!z && !z.hidden;
  });
}

/** Steht in der Tafel überhaupt etwas — ein Hinweis oder die Zeile „Meldungen ansehen“? */
function lageHatInhalt(): boolean {
  const m = document.getElementById(MELDUNGEN);
  return lageDa() || (!!m && !m.hidden);
}

export function lageOffen(): boolean {
  const f = document.getElementById(FENSTER);
  return !!f && !f.hidden;
}

let abbauen: (() => void) | null = null;

function schliesseLage(fokusZurueck = false): void {
  const f = document.getElementById(FENSTER);
  const g = document.getElementById(GLOCKE);
  abbauen?.();
  abbauen = null;
  if (f) f.hidden = true;
  g?.setAttribute("aria-expanded", "false");
  if (fokusZurueck) g?.focus();
}

/** Ein Besitzer hat seinen Schalter umgelegt: Tafel zuklappen, wenn leer; Glocke neu prüfen lassen. */
export function lageGeaendert(): void {
  if (lageOffen() && !lageHatInhalt()) schliesseLage();
  document.dispatchEvent(new Event(LAGE_GEAENDERT));
}

/** Das nächste Bedienelement nach `el` in der Tab-Reihe des Dokuments (sichtbar, nicht gesperrt) — oder null. */
function naechstesNach(el: HTMLElement): HTMLElement | null {
  const alle = [...document.querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex]")].filter(
    (e) => e.tabIndex >= 0 && !(e as HTMLButtonElement).disabled && e.getClientRects().length > 0,
  );
  const i = alle.indexOf(el);
  return i >= 0 ? (alle[i + 1] ?? null) : null;
}

export function oeffneLage(): void {
  const f = document.getElementById(FENSTER);
  const g = document.getElementById(GLOCKE);
  if (!f || !g || !lageDa()) return;
  f.hidden = false;
  g.setAttribute("aria-expanded", "true");

  const aufTaste = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      ev.stopPropagation();
      schliesseLage(true);
      return;
    }
    // 22.10.2026: das Fenster steht nicht mehr direkt hinter der Glocke im Dokument (es liegt neben #app, damit es über Vollseiten
    // aufgeht). Die Tab-Reihe bleibt trotzdem dieselbe: Glocke → erste Zeile, und Umschalt+Tab von der ersten Zeile zurück zur Glocke.
    if (ev.key !== "Tab") return;
    const ziele = [...f.querySelectorAll<HTMLElement>("button")].filter((b) => !b.hidden && !b.closest("[hidden]"));
    const erste = ziele[0];
    const letzte = ziele[ziele.length - 1];
    if (!erste || !letzte) return;
    if (!ev.shiftKey && document.activeElement === g) {
      ev.preventDefault();
      erste.focus();
    } else if (ev.shiftKey && document.activeElement === erste) {
      ev.preventDefault();
      g.focus();
    } else if (!ev.shiftKey && document.activeElement === letzte) {
      // Heraus aus dem Fenster: es schließt, der Fokus geht auf das, was im Dokument nach der Glocke kommt.
      ev.preventDefault();
      schliesseLage();
      naechstesNach(g)?.focus();
    }
  };
  const aufZeiger = (ev: Event): void => {
    const ziel = ev.target;
    if (ziel instanceof Node && (f.contains(ziel) || g.contains(ziel))) return;
    schliesseLage();
  };
  const aufFokus = (ev: FocusEvent): void => {
    const ziel = ev.target;
    if (ziel instanceof Node && (f.contains(ziel) || g.contains(ziel))) return;
    schliesseLage();
  };
  // Eine Hinweiszeile angetippt: zu, bevor ihr eigener Handler die Ansicht wechselt (Erfassungsphase am Fenster, also vor dem
  // Handler am Knopf). `detail === 0`: kein Zeigerklick, sondern Tastatur oder Hilfstechnik — dann zurück zur Glocke.
  const aufKlick = (ev: MouseEvent): void => {
    const ziel = ev.target;
    if (!(ziel instanceof Element)) return;
    if (!LAGE_ZEILEN.some((id) => document.getElementById(id)?.contains(ziel))) return;
    schliesseLage(ev.detail === 0);
  };
  f.addEventListener("click", aufKlick, true);
  document.addEventListener("keydown", aufTaste);
  document.addEventListener("pointerdown", aufZeiger, true);
  document.addEventListener("focusin", aufFokus);
  abbauen = () => {
    f.removeEventListener("click", aufKlick, true);
    document.removeEventListener("keydown", aufTaste);
    document.removeEventListener("pointerdown", aufZeiger, true);
    document.removeEventListener("focusin", aufFokus);
  };
}

/** Antippen der Glocke: die Lage auf-/zuklappen — oder, ohne Lage, gleich die Liste. */
async function klickeGlocke(): Promise<void> {
  if (!lageDa()) {
    const m = await import("./glocke.js");
    m.oeffneGlocke();
    return;
  }
  if (lageOffen()) schliesseLage();
  else oeffneLage();
}

/** „N neue Meldungen ansehen“: die Lage zu, die bestehende Liste auf. */
async function sehMeldungen(): Promise<void> {
  schliesseLage();
  const m = await import("./glocke.js");
  m.oeffneGlocke();
}

/** Einmal verdrahten (app.ts). */
export function verdrahteLage(): void {
  document.getElementById(GLOCKE)?.addEventListener("click", () => void klickeGlocke());
  document.getElementById(MELDUNGEN)?.addEventListener("click", () => void sehMeldungen());
}
